import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import React, { useEffect } from "react";

import { useGetCandidatePolicies } from "../../../../api/queries";

import type { CandidatePolicy } from "../../../../api/types";

export interface CurrentCarrierPolicySelectProps {
  /** Coverage the group belongs to; candidates are coverage-scoped (ADR 10.22). */
  coverageId: string;
  /** Optional plan type (e.g. "ppo") to narrow PT/PPT matching. */
  planType?: string;
  /** Currently selected broker policy UUID. */
  value?: string;
  /** Called with the selected policy UUID (or undefined when cleared). */
  onChange: (policyUuid: string | undefined) => void;
  disabled?: boolean;
  /**
   * Inline (in-grid) styling: borderless, no floating label, inherits the
   * grid font — so it matches the other workbench cell dropdowns (e.g. Requested
   * Commission) rather than looking like a boxed form field. Defaults to the
   * outlined form-field look used inside dialogs.
   */
  inline?: boolean;
  /**
   * Stable key (the plan group id) used to auto-select the single candidate
   * policy at most once. The in-grid header remounts on every refetch, which
   * would otherwise re-fire the auto-select and PATCH repeatedly.
   */
  autoSelectKey?: string;
}

const truncate = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

// Groups whose single-candidate policy has already been auto-selected. Module
// scope so it survives the header's remounts (see autoSelectKey above).
const autoSelectedGroups = new Set<string>();

/**
 * Clears the auto-select guard for a group so the single-candidate auto-select
 * can retry — call this when the persisting PATCH fails and the selection is
 * reverted, otherwise the group would never auto-select again.
 */
export const clearAutoSelectGuard = (key: string): void => {
  autoSelectedGroups.delete(key);
};

/** Display label for a candidate policy: "Carrier - PolicyNumber". */
export const policyLabel = (policy: CandidatePolicy): string =>
  `${policy.carrierName} - ${policy.policyNumber}`;

/**
 * Dropdown for selecting a current plan group's Current Carrier - Policy
 * (ADR 10.22). Fetches active in-force policies for the coverage and renders
 * them as "Carrier - PolicyNumber", truncated with a hover tooltip.
 *
 * Default-selection rule: exactly one match auto-selects; more than one
 * requires an explicit single selection.
 */
export const CurrentCarrierPolicySelect: React.FC<
  CurrentCarrierPolicySelectProps
> = ({
  coverageId,
  planType,
  value,
  onChange,
  disabled,
  inline = false,
  autoSelectKey,
}) => {
  const {
    data: policies = [],
    isLoading,
    isError,
  } = useGetCandidatePolicies(coverageId, planType);

  // Auto-select when exactly one policy matches and nothing is chosen yet.
  // Guarded by autoSelectKey so a remounting header fires the PATCH once
  // rather than looping while the refetch settles.
  useEffect(() => {
    if (value || policies.length !== 1) {
      return;
    }

    if (autoSelectKey) {
      if (autoSelectedGroups.has(autoSelectKey)) {
        return;
      }

      autoSelectedGroups.add(autoSelectKey);
    }

    onChange(policies[0].policyUuid);
  }, [policies, value, onChange, autoSelectKey]);

  let helperText: string | undefined;
  if (isError) {
    helperText = "Couldn't load policies. Try again.";
  } else if (!isLoading && policies.length === 0) {
    helperText = "No active policies found for this coverage.";
  }

  return (
    <TextField
      id="current-carrier-policy-textfield"
      name="currentCarrierPolicy"
      // In-grid: no floating label (the column header already says "Current
      // Policy") and a borderless variant so it matches the other cell
      // dropdowns; in dialogs: standard labelled, outlined form field.
      label={inline ? undefined : "Current Carrier - Policy"}
      variant={inline ? "standard" : "outlined"}
      select
      fullWidth
      margin={inline ? "none" : "dense"}
      value={value ?? ""}
      disabled={disabled || isLoading}
      error={isError}
      helperText={inline ? undefined : helperText}
      onChange={(e) => onChange(e.target.value || undefined)}
      sx={
        inline
          ? {
              // Match the workbench cell dropdowns (e.g. Current Commission):
              // no chevron, no underline, centered, grid font. The grey
              // current-group fill is applied by the full-height header Box, so
              // every input/select layer must be transparent to let it show.
              backgroundColor: "transparent",
              "& .MuiInputBase-root": { backgroundColor: "transparent" },
              "& .MuiInput-input": {
                font: "inherit",
                py: 0.25,
                backgroundColor: "transparent",
              },
              "& .MuiSelect-select": {
                font: "inherit",
                textAlign: "center",
                backgroundColor: "transparent",
                // Reclaim the space MUI reserves for the (now hidden) icon.
                paddingRight: "8px !important",
              },
              "& .MuiSelect-icon": { display: "none" },
            }
          : undefined
      }
      slotProps={{
        htmlInput: {
          id: "current-carrier-policy-input",
          name: "currentCarrierPolicy",
        },
        ...(inline
          ? {
              input: { disableUnderline: true },
              select: { displayEmpty: true, IconComponent: () => null },
            }
          : {}),
      }}
    >
      {/* Empty option so a selection can be cleared (onChange => undefined).
          Only in the dialog form fields; the in-grid dropdown omits it. */}
      {!inline && (
        <MenuItem value="">
          <em>None</em>
        </MenuItem>
      )}
      {policies.map((policy) => {
        const label = policyLabel(policy);

        return (
          <MenuItem key={policy.policyUuid} value={policy.policyUuid}>
            <Tooltip title={label}>
              <Box sx={truncate}>{label}</Box>
            </Tooltip>
          </MenuItem>
        );
      })}
    </TextField>
  );
};

CurrentCarrierPolicySelect.displayName = "CurrentCarrierPolicySelect";
export default CurrentCarrierPolicySelect;
